import { Box, createStyles, Stack } from '@mantine/core';
import { SELECTABLE_PROPERTY_OPTIONS } from '@repo/common/components/SelectableProperty/constants';
import type { IconNames } from '@repo/foundations';
import {
	Icon,
	MultiSelect,
	Select,
	TextInput,
	TextInputError,
	TextInputLabel,
	Title,
} from '@repo/foundations';
import type { ColorNames } from '@repo/theme/utils';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { ExpandCollapse } from '../../../components/ExpandCollapse/ExpandCollapse';
import { RichEditor } from '../../../components/RichEditor';
import PolicySection from '../../PolicyPage/PolicySection';
import { useCreatePolicyStore } from './useCreatePolicyStore';

const useStyles = createStyles(
	(theme, { hasError }: { hasError: boolean }) => ({
		input: {
			width: '100%',
			minHeight: theme.other.space[24],
			borderColor: theme.other.getColor('border/input/default'),
			padding: `${theme.spacing['2xs']} ${theme.spacing.sm}`,
			borderRadius: theme.radius.sm,
			borderWidth: 0.5,
			borderStyle: 'solid',
			backgroundColor: theme.other.getColor('surface/input/default'),
			...(hasError && {
				borderColor: theme.other.getColor('border/critical-secondary/default'),
				backgroundColor: theme.other.getColor('surface/critical/default'),
			}),
			'&:has(.ProseMirror-focused)': {
				boxShadow: theme.shadows.sm,
			},
		},
		editor: {
			'& .ProseMirror': {
				wordBreak: 'break-word',
				fontSize: theme.fontSizes.sm,
				minHeight: theme.other.space[24],
			},
		},
	})
);

function PropertiesStack() {
	const store = useCreatePolicyStore();
	const { classes, theme } = useStyles({ hasError: !!store.descriptionError });

	const handleDescriptionChange = useCallback(
		(value: string | undefined) => {
			store.setDescription(value ?? '');
		},
		[store]
	);

	return (
		<Stack p={0} spacing="lg">
			<Title size="md">Policy properties</Title>
			<PolicySection
				content={
					<Stack spacing="md">
						<TextInput
							size="sm"
							label="Name"
							value={store.name}
							onChange={(e) => store.setName(e.target.value)}
							error={store.nameError}
						/>
						<Stack spacing={theme.spacing['3xs']}>
							<TextInputLabel label="Description" inputId="" />
							<Box className={classes.input}>
								<ExpandCollapse maxHeight={200} enabled>
									<RichEditor
										emptyDocPlaceholder="Add policy description..."
										readOnly={false}
										initialValue={store.description}
										onChangeCallback={handleDescriptionChange}
										disableTopGap
										limited
										autoFocus={false}
										dataTestId="description"
										className={classes.editor}
									/>
								</ExpandCollapse>
							</Box>
							{store.descriptionError && (
								<TextInputError error={store.descriptionError} />
							)}
						</Stack>
						<MultiSelect
							label="Frameworks"
							placeholder="Select"
							data={SELECTABLE_PROPERTY_OPTIONS.complianceFramework.map(
								(item) => ({
									value: item.value as string,
									label: item.label as string,
								})
							)}
							value={store.frameworks}
							setValue={store.setFrameworks}
							dropdownPosition="flip"
							optional
						/>
						<Select
							label="Severity"
							placeholder="Select"
							data={SELECTABLE_PROPERTY_OPTIONS.severity.map((item) => ({
								value: item.value as string,
								label: item.label as string,
								iconName: item.iconName as IconNames,
								color: item.color as ColorNames,
							}))}
							help="Higher severity means more frequent scans (High: 2 hrs, Medium: 12 hrs, Low: 48 hrs)"
							value={store.severity}
							onChange={store.setSeverity}
							renderIcon={(item) => (
								<Icon name={item.iconName} color={item.color} />
							)}
							error={store.severityError}
						/>
					</Stack>
				}
			/>
		</Stack>
	);
}

const PropertiesStackWithObserver = observer(PropertiesStack);
export default PropertiesStackWithObserver;
